import type {
  CreateOfferRequest,
  OfferDetailResponse,
  UpdateOfferRequest,
} from "@codway-dev/shared";
import type { ButtonStyle } from "@codway-dev/ui";
import type { DownsellTriggerType } from "../pages/sales-booster/downsell-editor/DownsellEditorConfig";
import type { BackgroundImagePreset } from "../pages/sales-booster/downsell-editor/DownsellEditorConfig";
import {
  buildTriggers,
  readTriggers,
  type TriggerProduct,
} from "./triggers.mapping";

// ─── Types ───────────────────────────────────────────────────────────────────

export type DiscountAction =
  | "none"
  | "percentage_off"
  | "fixed_price_per_item"
  | "fixed_amount_off";

export interface DownsellEditorState {
  internalName: string;
  isActive: boolean;

  triggerType: DownsellTriggerType;
  triggerProducts: TriggerProduct[];

  timesShownLimit: number;

  backgroundImagePreset: BackgroundImagePreset;
  backgroundColor: string;

  borderRadius: number;
  borderWidth: number;
  borderColor: string;

  titleTemplate: string;
  titleColor: string;
  titleSize: number;

  subtitleTemplate: string;
  subtitleColor: string;
  subtitleSize: number;

  discountBadgeText: string;
  discountBadgeTextColor: string;
  discountBadgeDiscountColor: string;
  discountBadgeSize: number;

  ctaAcceptStyle: ButtonStyle;
  ctaDeclineStyle: ButtonStyle;

  pricingAction: DiscountAction;
  pricingValue: number;
}

// ─── Defaults ────────────────────────────────────────────────────────────────

export const DEFAULT_ACCEPT_STYLE: ButtonStyle = {
  text: "COMPLETA TU PEDIDO CON {discount} DE DESCUENTO",
  backgroundColor: "linear-gradient(90deg, #e91e8c, #e9581e)",
  textColor: "rgba(255,255,255,1)",
  borderColor: "rgba(0,0,0,1)",
  borderWidth: 0,
  borderRadius: 50,
  textSize: 14,
  shadow: 0,
  animation: "none",
};

export const DEFAULT_DECLINE_STYLE: ButtonStyle = {
  text: "No gracias",
  backgroundColor: "rgba(255,255,255,1)",
  textColor: "rgba(0,0,0,1)",
  borderColor: "rgba(0,0,0,1)",
  borderWidth: 1,
  borderRadius: 50,
  textSize: 14,
  shadow: 0,
  animation: "none",
};

export const DEFAULT_DOWNSELL_EDITOR_STATE: DownsellEditorState = {
  internalName: "Nuevo downsell",
  isActive: true,

  triggerType: "all",
  triggerProducts: [],

  timesShownLimit: 1,

  backgroundImagePreset: "preset_1",
  backgroundColor: "#ffffff",

  borderRadius: 16,
  borderWidth: 0,
  borderColor: "rgba(0,0,0,1)",

  titleTemplate: "¡Espera!",
  titleColor: "rgba(0,0,0,1)",
  titleSize: 13,

  subtitleTemplate: "¡Tenemos una oferta para ti!",
  subtitleColor: "rgba(45,45,45,1)",
  subtitleSize: 13,

  discountBadgeText: "OBTÉN UN DESCUENTO EXTRA EN TU PEDIDO:",
  discountBadgeTextColor: "rgba(0,0,0,1)",
  discountBadgeDiscountColor: "rgba(255,255,255,1)",
  discountBadgeSize: 48,

  ctaAcceptStyle: DEFAULT_ACCEPT_STYLE,
  ctaDeclineStyle: DEFAULT_DECLINE_STYLE,

  pricingAction: "percentage_off",
  pricingValue: 10,
};

// ─── ButtonStyle (UI camelCase) ↔ ButtonStyleSchema (BE snake_case) ──────────

function buttonStyleToSchema(s: ButtonStyle) {
  return {
    background_color: s.backgroundColor,
    text_color: s.textColor,
    border_color: s.borderColor,
    border_width: s.borderWidth,
    border_radius: s.borderRadius,
    text_size: s.textSize,
    shadow: s.shadow,
    animation: s.animation,
  };
}

function schemaToButtonStyle(
  styles: Record<string, unknown> | undefined,
  text: string,
  fallback: ButtonStyle
): ButtonStyle {
  const s = styles ?? {};
  const animation = s.animation as ButtonStyle["animation"] | undefined;
  return {
    text,
    backgroundColor: (s.background_color as string) ?? fallback.backgroundColor,
    textColor: (s.text_color as string) ?? fallback.textColor,
    borderColor: (s.border_color as string) ?? fallback.borderColor,
    borderWidth: (s.border_width as number) ?? fallback.borderWidth,
    borderRadius: (s.border_radius as number) ?? fallback.borderRadius,
    textSize: (s.text_size as number) ?? fallback.textSize,
    shadow: (s.shadow as number) ?? fallback.shadow,
    animation: animation ?? fallback.animation,
  };
}

// ─── Pricing logic ────────────────────────────────────────────────────────────

function buildPricingLogic(action: DiscountAction, value: number) {
  switch (action) {
    case "percentage_off":
      return { action: "percentage_off" as const, value };
    case "fixed_price_per_item":
      return { action: "fixed_price_per_item" as const, amount: value };
    case "fixed_amount_off":
      return { action: "fixed_amount_off" as const, amount: value };
    default:
      return { action: "none" as const };
  }
}

function readPricingLogic(pl: OfferDetailResponse["config"]["pricing_logic"]): {
  pricingAction: DiscountAction;
  pricingValue: number;
} {
  switch (pl.action) {
    case "percentage_off":
      return { pricingAction: "percentage_off", pricingValue: pl.value };
    case "fixed_price_per_item":
      return { pricingAction: "fixed_price_per_item", pricingValue: pl.amount };
    case "fixed_amount_off":
      return { pricingAction: "fixed_amount_off", pricingValue: pl.amount };
    default:
      return { pricingAction: "none", pricingValue: 0 };
  }
}

// ─── Build display_data for backend ──────────────────────────────────────────

function buildDisplayData(state: DownsellEditorState) {
  return {
    component_type: "modal" as const,
    title_template: state.titleTemplate,
    subtitle_template: state.subtitleTemplate || undefined,
    cta_accept_text: state.ctaAcceptStyle.text,
    cta_accept_styles: buttonStyleToSchema(state.ctaAcceptStyle),
    cta_decline_text: state.ctaDeclineStyle.text,
    cta_decline_styles: buttonStyleToSchema(state.ctaDeclineStyle),
    enable_quantity_selector: false,
    hide_close_icon: true,
    hide_variant_selector: true,
    background_image_preset: state.backgroundImagePreset,
    times_shown_limit: state.timesShownLimit,
    discount_badge: {
      text: state.discountBadgeText || undefined,
      text_color: state.discountBadgeTextColor || undefined,
      discount_color: state.discountBadgeDiscountColor || undefined,
      size: state.discountBadgeSize,
    },
    styles: {
      background_color: state.backgroundColor || undefined,
      title_color: state.titleColor || undefined,
      title_size: state.titleSize,
      subtitle_color: state.subtitleColor || undefined,
      subtitle_size: state.subtitleSize,
      border_radius: state.borderRadius,
      border_width: state.borderWidth,
      border_color: state.borderColor || undefined,
    },
  };
}

// ─── Public API ───────────────────────────────────────────────────────────────

export function stateToCreateRequest(
  state: DownsellEditorState
): CreateOfferRequest {
  return {
    internal_name: state.internalName,
    is_active: state.isActive,
    priority: 0,
    config: {
      display_type: "modal",
      placement: "on_close",
      display_data: buildDisplayData(state),
      pricing_logic: buildPricingLogic(state.pricingAction, state.pricingValue),
    },
    target: { type: "synthetic" },
    triggers: buildTriggers(state.triggerType, state.triggerProducts),
  };
}

export function stateToUpdateRequest(
  state: DownsellEditorState,
  originalConfig?: OfferDetailResponse["config"] | null
): UpdateOfferRequest {
  const create = stateToCreateRequest(state);

  if (
    originalConfig?.display_type === "modal" &&
    create.config.display_type === "modal"
  ) {
    return {
      ...create,
      config: {
        ...(originalConfig as Record<string, unknown>),
        ...create.config,
        display_data: {
          ...(originalConfig.display_data as Record<string, unknown>),
          ...create.config.display_data,
        } as typeof create.config.display_data,
      },
    };
  }

  return create;
}

export function offerToState(offer: OfferDetailResponse): DownsellEditorState {
  const config = offer.config;

  if (config.display_type !== "modal" || config.placement !== "on_close") {
    throw new Error(
      `Este editor solo admite downsells (modal con placement "on_close"). La oferta "${offer.internal_name}" no es un downsell.`
    );
  }

  const dd = config.display_data as Record<string, unknown>;
  const styles = (dd.styles ?? {}) as Record<string, unknown>;
  const discountBadge = (dd.discount_badge ?? {}) as Record<string, unknown>;
  const ctaAcceptStyles = (dd.cta_accept_styles ?? {}) as Record<
    string,
    unknown
  >;
  const ctaDeclineStyles = (dd.cta_decline_styles ?? {}) as Record<
    string,
    unknown
  >;

  const triggers = readTriggers(offer.triggers);
  const pricing = readPricingLogic(config.pricing_logic);

  const D = DEFAULT_DOWNSELL_EDITOR_STATE;

  return {
    internalName: offer.internal_name,
    isActive: offer.is_active,

    triggerType: triggers.triggerType,
    triggerProducts: triggers.triggerProducts,

    timesShownLimit: (dd.times_shown_limit as number) ?? D.timesShownLimit,

    backgroundImagePreset:
      (dd.background_image_preset as BackgroundImagePreset) ??
      D.backgroundImagePreset,
    backgroundColor: (styles.background_color as string) ?? D.backgroundColor,

    borderRadius: (styles.border_radius as number) ?? D.borderRadius,
    borderWidth: (styles.border_width as number) ?? D.borderWidth,
    borderColor: (styles.border_color as string) ?? D.borderColor,

    titleTemplate: (dd.title_template as string) ?? D.titleTemplate,
    titleColor: (styles.title_color as string) ?? D.titleColor,
    titleSize: (styles.title_size as number) ?? D.titleSize,

    subtitleTemplate: (dd.subtitle_template as string) ?? D.subtitleTemplate,
    subtitleColor: (styles.subtitle_color as string) ?? D.subtitleColor,
    subtitleSize: (styles.subtitle_size as number) ?? D.subtitleSize,

    discountBadgeText: (discountBadge.text as string) ?? D.discountBadgeText,
    discountBadgeTextColor:
      (discountBadge.text_color as string) ?? D.discountBadgeTextColor,
    discountBadgeDiscountColor:
      (discountBadge.discount_color as string) ?? D.discountBadgeDiscountColor,
    discountBadgeSize: (discountBadge.size as number) ?? D.discountBadgeSize,

    ctaAcceptStyle: schemaToButtonStyle(
      ctaAcceptStyles,
      (dd.cta_accept_text as string) ?? D.ctaAcceptStyle.text,
      DEFAULT_ACCEPT_STYLE
    ),
    ctaDeclineStyle: schemaToButtonStyle(
      ctaDeclineStyles,
      (dd.cta_decline_text as string) ?? D.ctaDeclineStyle.text,
      DEFAULT_DECLINE_STYLE
    ),

    pricingAction: pricing.pricingAction,
    pricingValue: pricing.pricingValue,
  };
}
